import { hexToRGB, rgColor } from './colorUtils';
import { getCurrentXFDFDateString, parseXFDFDate } from './dateUtils';
import { getChildByTagName } from './htmlUtils';
import type { Point } from './mathUtils';

export class XFDFAnnot {
    private annot: Element;

    constructor(xmlElement: Element) {
        this.annot = xmlElement;
    }

    get type(): string {
        return this.annot.tagName;
    }
    get page(): number {
        return Number(this.annot.getAttribute('page') ?? '0');
    }
    get author(): string {
        return this.annot.getAttribute('title') ?? '';
    }

    setPage(pageIndex: number) {
        this.annot.setAttribute('page', pageIndex.toString());
    }
    /** Array format: [llx, lly, urx, ury] (lower-left x, lower-left y, upper-right x, upper-right y) */
    getRect(): number[] {
        return (this.annot.getAttribute('rect')?.split(',') ?? ['0', '500', '200', '1000']).map(x =>
            Number(x),
        );
    }
    /** Array format: [llx, lly, urx, ury] (lower-left x, lower-left y, upper-right x, upper-right y) */
    setRect(rect: number[]) {
        this.annot.setAttribute('rect', rect.join(','));
    }
    isCallout(): boolean {
        return this.type === 'freetext' && this.annot.getAttribute('IT') === 'FreeTextCallout';
    }
    isPolyline(): boolean {
        return this.type === 'polyline' || this.type === 'polygon';
    }
    getCalloutArrowCoords(): {
        start: Point;
        knee: Point | null;
        end: Point;
    } | null {
        if (!this.isCallout()) return null;

        const coords = (this.annot.getAttribute('callout')?.split(',') ?? []).map(s => Number(s));

        // 4 coords: straight arrow
        if (coords.length === 4) {
            return {
                start: { x: coords[0], y: coords[1] },
                knee: null,
                end: { x: coords[2], y: coords[3] },
            };
        }

        // 6 coords: bent arrow with knee point
        if (coords.length === 6) {
            return {
                start: { x: coords[0], y: coords[1] },
                knee: { x: coords[2], y: coords[3] },
                end: { x: coords[4], y: coords[5] },
            };
        }

        return null;
    }
    setCalloutArrowCoords(coords: { start: Point; knee: Point | null; end: Point }) {
        if (!this.isCallout()) return;

        const newValue = [
            coords.start.x, coords.start.y,
            ...(coords.knee ? [coords.knee.x, coords.knee.y] : []),
            coords.end.x, coords.end.y
        ];

        this.annot.setAttribute('callout', newValue.join(','));
    }
    getPolyCoords(): Point[] | null {
        if (!this.isPolyline()) return null;

        const verticesEl = getChildByTagName(this.annot, 'vertices');

        if (!verticesEl) return null;

        const pairs = verticesEl.innerHTML.split(';');
        const coords = pairs.map(pair => {
            const [x, y] = pair.split(',');
            return { x: Number(x), y: Number(y) };
        });
        return coords;
    }
    setPolyCoords(coords: Point[]) {
        if (!this.isPolyline()) return;

        const newValue = coords.map(coord => [coord.x, coord.y].join(','));

        const verticesEl = getChildByTagName(this.annot, 'vertices');
        verticesEl && (verticesEl.innerHTML = newValue.join(';'));
    }
    getColor(): string {
        return this.annot.getAttribute('color') ?? '#000000';
    }
    setColor(color: string) {
        this.annot.setAttribute('color', color);
    }
    getFillColor(): string {
        return this.annot.getAttribute('interior-color') ?? 'transparent';
    }
    setFillColor(color: string) {
        if (['square', 'circle', 'polygon'].includes(this.type)) {
            this.annot.setAttribute('interior-color', color);
        }
    }
    getTextColor(): string {
        return this.annot.getAttribute('TextColor') ?? '#000000';
    }
    setTextColor(color: string) {
        if (this.annot.hasAttribute('TextColor')) {
            this.annot.setAttribute('TextColor', color);
        }
    }
    getModifiedDate(): Date | null {
        const date = this.annot.getAttribute('date');
        return date ? parseXFDFDate(date) : null;
    }
    setModifiedDate() {
        this.annot.setAttribute('date', getCurrentXFDFDateString());
    }
    getCreationDate(): Date | null {
        const date = this.annot.getAttribute('creationdate');
        return date ? parseXFDFDate(date) : null;
    }
    setCreationDate() {
        this.annot.setAttribute('creationdate', getCurrentXFDFDateString());
    }
    setAuthor(author: string) {
        this.annot.setAttribute('title', author);
    }
    getId(): string {
        return this.annot.getAttribute('name') ?? '';
    }
    setId(guid: string) {
        this.annot.setAttribute('name', guid);
    }
    removeColor() {
        this.annot.removeAttribute('color');
    }
    /** Freetext bordercolor */
    setDefaultAppearanceColor(color: string) {
        const defaultappearance = Array.from(this.annot.children).find(
            el => el.tagName === 'defaultappearance',
        );

        if (!defaultappearance) return;

        // <defaultappearance>0.5 1 0 rg /Cousine 20 Tf</defaultappearance>
        const font = defaultappearance.innerHTML.split('rg')[1].trim();

        const { r, g, b } = hexToRGB(color);
        defaultappearance.innerHTML = `${rgColor(r)} ${rgColor(g)} ${rgColor(b)} rg ${font}`;
    }
    setDefaultStyleColor(color: string) {
        const defaultstyle = Array.from(this.annot.children).find(
            el => el.tagName === 'defaultstyle',
        );

        if (!defaultstyle) return;

        const colorAttr = defaultstyle.innerHTML
            .split(';')
            .find(attr => attr.trim().startsWith('color:'));

        if (colorAttr) {
            defaultstyle.innerHTML = defaultstyle.innerHTML.replace(colorAttr, `color: ${color}`);
        }
    }
    /** Callout textcolor */
    setStyleColors(color: string) {
        const contentsrichtext = Array.from(this.annot.children).find(
            el => el.tagName === 'contents-richtext',
        );

        if (!contentsrichtext) return;

        Array.from(contentsrichtext.getElementsByTagName('*')).forEach(el => {
            const styleAttr = el.getAttribute('style');

            if (!styleAttr) return;

            const colorAttr = styleAttr.split(';').find(attr => attr.startsWith('color:'));

            if (colorAttr) {
                el.setAttribute('style', styleAttr.replace(colorAttr, `color:${color}`));
            }
        });
    }
    /** Delete from XFDF document */
    delete() {
        this.annot.remove();
    }
    /** Returns wether the annotation was created or modified during this session. */
    isFromSession(sessionStarttime: number): boolean {
        const creationDate = this.getCreationDate();
        if (creationDate && creationDate.getTime() > sessionStarttime) {
            return true;
        }

        const modifiedDate = this.getModifiedDate();
        if (modifiedDate && modifiedDate.getTime() > sessionStarttime) {
            return true;
        }

        return false;
    }
}

export function parseXFDFDoc(xfdf: string): { xfdfDoc: Document; xfdfAnnots: XFDFAnnot[] } {
    const parser = new DOMParser();
    const xfdfDoc = parser.parseFromString(xfdf, 'text/xml');

    const annotsTag = xfdfDoc.getElementsByTagName('annots')?.[0];
    const xfdfAnnots = Array.from(annotsTag?.children ?? []).map(el => new XFDFAnnot(el));

    return { xfdfDoc, xfdfAnnots };
}

export function serializeXFDFDoc(xfdfDoc: Document): string {
    return new XMLSerializer().serializeToString(xfdfDoc);
}
